import React from 'react';
import { Chip, Avatar, Spinner } from '@heroui/react';
import { IdView, Typography, CustomTooltip } from '../../../../components';
import AccountTable from '../../../../components/AccountTable';
import { useTranslations } from '../../../../contexts/translation.context';
import { OtherOffersTableProps, OtherOfferData } from '../types';
import { formatPrice, formatFansRange, getStatusColor, getStatusDotColor } from '../utils';
import { getPlatformIcon, transformPlatform, getFilterIcon } from '../../../../utils';
import {
  CalendarIcon,
  DollarRoundedIc,
  GroupUserIc,
  InfoCircle,
} from '../../../../components/icons';
import { formatTimeAgo } from '../../../../utils/formatTimeAgo';
const getFilterOptions = (t: any) => [
  { key: 'all', label: t('proposals.allSources'), icon: getFilterIcon('all', 16) },
  {
    key: 'only_fans',
    label: t('proposals.onlyFans'),
    icon: getFilterIcon('only_fans', 16, '#A8A8B0'),
  },
  { key: 'reddit', label: t('proposals.reddit'), icon: getFilterIcon('reddit', 16) },
  {
    key: 'instagram',
    label: t('proposals.instagram'),
    icon: getFilterIcon('instagram', 16, '#A8A8B0'),
  },
  { key: 'tiktok', label: t('proposals.tiktok'), icon: getFilterIcon('tiktok', 16) },
  { key: 'google', label: t('proposals.google'), icon: getFilterIcon('google', 16, '#A8A8B0') },
  { key: 'telegram', label: t('proposals.telegram'), icon: getFilterIcon('telegram', 16) },
  { key: 'twitter', label: t('proposals.twitter'), icon: getFilterIcon('twitter', 16, '#A8A8B0') },
  { key: 'youtube', label: t('proposals.youtube'), icon: getFilterIcon('youtube', 16) },
  { key: 'another', label: t('proposals.other'), icon: getFilterIcon('another', 16) },
];
const OtherOffersTable: React.FC<OtherOffersTableProps> = ({
  data,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onOrder,
  onCopyOffer,
}) => {
  const { t } = useTranslations();
  const columns = [
    { name: t('salesFans.tableColumns.id'), key: 'id' },
    { name: t('salesFans.tableColumns.marketer'), key: 'creator' },
    { name: t('salesFans.tableColumns.offer'), key: 'offer' },
    { name: t('salesFans.tableColumns.description'), key: 'description' },
  ];
  const getPartnerLevelStyle = (rating: number) => {
    console.log('rating', rating);
    if (rating >= 40)
      return {
        bg: 'bg-accent/20',
        text: 'text-accent',
        label: t('proposals.partnerLevels.expert'),
      };
    if (rating >= 35)
      return { bg: 'bg-auth-ink', text: 'text-white', label: t('proposals.partnerLevels.pro') };
    if (rating >= 30)
      return {
        bg: 'bg-success/20',
        text: 'text-success',
        label: t('proposals.partnerLevels.new'),
      };
    return {
      bg: 'bg-success/20',
      text: 'text-success',
      label: t('proposals.partnerLevels.new'),
    };
    // return { bg: 'bg-[#EEEEEF]', text: 'text-auth-muted', label: t('proposals.partnerLevels.base') };
  };

  const getTooltipText = (tag: string) => {
    switch (tag.toLowerCase()) {
      case 'free':
        return t('salesFans.freeToolTip');
      case 'free-trial':
        return t('salesFans.freeTrialToolTip');
      case 'paid':
        return t('salesFans.paidToolTip');
      default:
        return null;
    }
  };

  const renderCell = (item: OtherOfferData, columnKey: string) => {
    switch (columnKey) {
      case 'id':
        return <IdView textToCopy={item.id}>{item.id.slice(-4)}</IdView>;

      case 'creator':
        const partnerLevel = getPartnerLevelStyle(item.owner.averageRating);
        return (
          <div className="flex items-center gap-4">
            <Avatar
              src={item.owner.avatar}
              alt={item.owner.name}
              size="sm"
              showFallback
              name={item.owner.name?.slice(0, 1)}
              className="w-10 h-10 rounded-3"
            />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <Typography variant="body2" className="text-gray-900 font-medium">
                  {item.owner.name}
                </Typography>
                <span
                  className={`px-2 py-1 rounded-full !h-6 text-xs font-regular ${partnerLevel.bg} ${partnerLevel.text}`}
                >
                  {partnerLevel.label}
                </span>
              </div>
              <div className="flex items-center gap-1 text-gray-500 text-sm">
                <CalendarIcon />
                {/* <Typography variant='body3' className="text-auth-muted">{formatTimeAgo(item.lastUpdated)}</Typography> */}
                <CustomTooltip
                  content={
                    <div className="flex flex-col">
                      <Typography variant="body3" className="text-auth-muted">
                        {t('salesFans.tableData.updatedAt')}:
                      </Typography>
                      <Typography variant="body2">{formatTimeAgo(item.lastUpdated, t)}</Typography>
                    </div>
                  }
                  placement="top"
                  className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
                >
                  <Typography variant="body3" className="text-auth-muted">
                    {formatTimeAgo(item.lastUpdated, t)}
                  </Typography>
                </CustomTooltip>
              </div>
            </div>
          </div>
        );

      case 'offer':
        return (
          <div className="flex flex-col gap-1 items-start">
            <div className="flex items-center gap-1 px-2 py-1 border !h-6 border-[1.4px] border-auth-strength-none rounded-xl">
              {item.source === 'another' ? (
                <Typography variant="body3">
                  {item.other_source_name || t('salesFans.labels.otherSource')}
                </Typography>
              ) : (
                <>
                  {getPlatformIcon(item.source)}
                  <Typography variant="body3">{transformPlatform(item.source)}</Typography>
                </>
              )}
              {item.description && (
                <>
                  <div className="w-[1px] h-3 bg-auth-strength-none mx-1"></div>
                  <Typography variant="body3">{item?.of_models}</Typography>
                  <CustomTooltip
                    content={item.description}
                    placement="top"
                    className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
                  >
                    <div className="cursor-pointer flex items-center">
                      <InfoCircle />
                    </div>
                  </CustomTooltip>
                </>
              )}
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <DollarRoundedIc />
                <span className="text-sm font-semibold text-gray-900">
                  {formatPrice(item.price.amount)}
                </span>
                <Typography variant="body3" className="text-auth-muted">
                  {item.offerType === 'subscription'
                    ? t('salesFans.tableData.perMonthPrice')
                    : t('salesFans.tableData.perFan')}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <GroupUserIc />
                {/* У офферов-подписок нет диапазона "всего подписчиков" — только объём в месяц. */}
                {item.offerType === 'subscription' ? (
                  <Typography variant="body3" className="text-auth-muted">
                    ~{item.fansRange.perDay} {t('salesFans.tableData.perMonth')}
                  </Typography>
                ) : (
                  <>
                    <span className="text-sm font-semibold text-gray-900">
                      {item.fansRange.min}-{item.fansRange.max || '∞'}
                    </span>
                    <Typography variant="body3" className="text-auth-muted">
                      /~{item.fansRange.perDay} {t('salesFans.tableData.perDay')}
                    </Typography>
                  </>
                )}
              </div>
            </div>
          </div>
        );

      case 'description':
        return (
          <div className="flex flex-col gap-1">
            <div className="flex gap-1 items-center">
              {item.tags.map((tag: string, index: number) => {
                const tooltipText = getTooltipText(tag);
                const TagComponent = (
                  <Typography
                    variant="label3"
                    key={index}
                    className={`${tag === 'free' ? '!uppercase' : '!capitalize'} font-semibold`}
                  >
                    {tag}
                  </Typography>
                );

                return tooltipText ? (
                  <CustomTooltip
                    key={index}
                    content={tooltipText}
                    placement="top"
                    className="w-[18.75rem] md:w-[27.25rem] break-all rounded-2 py-2 !px-4"
                  >
                    {TagComponent}
                  </CustomTooltip>
                ) : (
                  TagComponent
                );
              })}
              {/* <ResponsiveTooltip 
                content={item?.requirements || t('proposals.noRequirements')}
                placement="top"
                className="max-w-[18.75rem] break-all rounded-2 py-2 !px-4"
              >
                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-neutral-12 rounded-full cursor-pointer hover:bg-[#e0e0e0] transition-colors">
                  <span className="text-xs font-regular text-auth-muted">{t('proposals.requirements')}</span>
                  <InfoCircle/>
                </div>
              </ResponsiveTooltip> */}
            </div>
            <div>
              <Typography variant="body2" className="break-all">
                {item.description}
              </Typography>
            </div>
            <div className="flex  gap-4">
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-success rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.active')}:{' '}
                  <Typography variant="label3">{item.orders.active}</Typography>
                </Typography>
              </div>
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-partner rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.completed')}:{' '}
                  <Typography variant="label3">{item.orders.completed}</Typography>
                </Typography>
              </div>
              <div className="flex items-center gap-1">
                <div className="size-[0.4375rem] bg-neutral-500 rounded-full"></div>
                <Typography variant="body3" className="text-auth-muted">
                  {t('salesFans.tableData.cancelled')}:{' '}
                  <Typography variant="label3">{item.orders.cancelled}</Typography>
                </Typography>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner
          size="lg"
          classNames={{
            circle1: '!border-b-partner',
            circle2: '!border-b-partner/50',
          }}
        />
      </div>
    );
  }

  return (
    <AccountTable
      data={data}
      columns={columns}
      renderCell={renderCell}
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      currentPage={currentPage}
      currentPageSize={pageSize}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      emptyContent={
        <div className="text-center py-8">
          <div className="text-gray-500">{t('salesFans.tableData.noOffersToDisplay')}</div>
        </div>
      }
    />
  );
};

export default OtherOffersTable;
